import type { ReactNode } from 'react';
import { useStakko } from '../context/StakkoProvider';
import type { StakkoPanelView, StakkoTheme } from '../types';
import {
  BellIcon,
  CheckIcon,
  CoinsIcon,
  GridIcon,
  MoonIcon,
  SettingsIcon,
  SparklesIcon,
  SunIcon,
  UsersIcon,
  StakkoMarkIcon,
} from './icons';
import { StakkoAvatar } from './primitives';

export interface StakkoPrimaryAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
}

export interface StakkoRailProps {
  view: StakkoPanelView | null;
  onOpen: (view: StakkoPanelView) => void;
  primaryAction?: StakkoPrimaryAction;
}

function RailButton({
  label,
  icon,
  active,
  badge,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  active: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`stakko-rail-btn${active ? ' is-active' : ''}`}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {icon}
      {badge && badge > 0 ? (
        <span className="stakko-rail-badge">{badge > 99 ? '99+' : badge}</span>
      ) : null}
      <span className="stakko-rail-tooltip">{label}</span>
    </button>
  );
}

function nextTheme(theme: StakkoTheme): StakkoTheme {
  if (theme === 'auto') return 'dark';
  if (theme === 'dark') return 'light';
  return 'auto';
}

export function StakkoRail({ view, onOpen, primaryAction }: StakkoRailProps) {
  const {
    user,
    profile,
    wallet,
    incomingRequests,
    unreadNotifications,
    theme,
    setTheme,
    appId,
  } = useStakko();

  const name = profile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Přihlásit se';
  const photoURL = profile?.photoURL || user?.photoURL || null;
  const avatarName = user ? name : 'V';
  const avatarPhoto = user ? photoURL : null;

  return (
    <aside className="stakko-rail" aria-label="Stakko účet a akce">
      <button
        type="button"
        className={`stakko-rail-avatar${view === 'account' || view === 'auth' ? ' is-active' : ''}`}
        onClick={() => onOpen(user ? 'account' : 'auth')}
        title={user ? name : 'Přihlásit se'}
        aria-label={user ? `Účet: ${name}` : 'Přihlásit se'}
      >
        <StakkoAvatar name={avatarName} photoURL={avatarPhoto} size={38} />
        <span className={`stakko-presence${user ? ' is-online' : ''}`} />
      </button>

      <div className="stakko-rail-divider" />

      {primaryAction ? (
        <button
          type="button"
          className="stakko-rail-primary"
          onClick={primaryAction.onClick}
          title={primaryAction.label}
          aria-label={primaryAction.label}
        >
          {primaryAction.icon}
          <span className="stakko-rail-tooltip">{primaryAction.label}</span>
        </button>
      ) : null}

      <RailButton
        label="Aplikace"
        icon={<GridIcon size={21} />}
        active={view === 'apps'}
        onClick={() => onOpen('apps')}
      />
      <RailButton
        label="Kredity"
        icon={<CoinsIcon size={21} />}
        active={view === 'credits'}
        badge={wallet.balance > 0 && wallet.balance <= 99 ? wallet.balance : undefined}
        onClick={() => onOpen('credits')}
      />
      <RailButton
        label="Tarify"
        icon={<SparklesIcon size={21} />}
        active={view === 'plans'}
        onClick={() => onOpen('plans')}
      />
      <RailButton
        label="Přátelé"
        icon={<UsersIcon size={21} />}
        active={view === 'friends'}
        badge={incomingRequests.length}
        onClick={() => onOpen('friends')}
      />
      <RailButton
        label="Oznámení"
        icon={<BellIcon size={21} />}
        active={view === 'notifications'}
        badge={unreadNotifications}
        onClick={() => onOpen('notifications')}
      />
      <RailButton
        label="Úkoly"
        icon={<CheckIcon size={21} />}
        active={view === 'todos'}
        onClick={() => onOpen('todos')}
      />

      <div className="stakko-rail-spacer" />

      <RailButton
        label="Nastavení"
        icon={<SettingsIcon size={21} />}
        active={view === 'settings'}
        onClick={() => onOpen('settings')}
      />

      <button
        type="button"
        className="stakko-rail-btn stakko-rail-theme"
        onClick={() => setTheme(nextTheme(theme))}
        title={`Vzhled: ${theme === 'auto' ? 'auto' : theme === 'dark' ? 'tmavý' : 'světlý'}`}
        aria-label="Přepnout vzhled"
      >
        {theme === 'dark' ? <MoonIcon size={20} /> : <SunIcon size={20} />}
        <span className="stakko-rail-tooltip">
          {theme === 'auto' ? 'Vzhled: automatický' : theme === 'dark' ? 'Vzhled: tmavý' : 'Vzhled: světlý'}
        </span>
      </button>

      <a
        className="stakko-rail-mark"
        href="https://stakko.cz"
        target="_blank"
        rel="noreferrer"
        title="Stakko"
        aria-label={`Stakko – aktuální aplikace ${appId}`}
      >
        <StakkoMarkIcon size={18} />
      </a>
    </aside>
  );
}
